import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewListIcon from '@mui/icons-material/ViewList';
import PePageHeader from '../components/PePageHeader';
import ProjectNavButtons from '../components/ProjectNavButtons';
import { projectEssentialApi, type SeminarEventItem } from '../services/api';

// ---------------------------------------------------------------------------
// Seminars & Events page
//   • Default view: Calendar (month grid with event indicators)
//   • Alternate view: List
//   • Both views are paginated per month — Prev/Next chevrons step the
//     "cursor" by one calendar month.
//   • Data is fetched from `/api/project-essential/events`, which already
//     applies the seminars-page category whitelist server-side. The
//     client whitelist below is kept as a defence-in-depth filter so a
//     relaxed backend never leaks an off-topic event into this surface.
// ---------------------------------------------------------------------------

// Whitelist of category codes that this page is allowed to display.
// Mirrors the server-side `ALLOWED` set in `routes/projectEssential.ts`.
const ALLOWED_EVENT_CATEGORIES = new Set(['C17046', 'C17047', 'C17048']);

type ViewMode = 'calendar' | 'list';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface MonthCursor {
  year: number;
  month: number; // 0-indexed (Jan = 0)
}

function todayCursor(): MonthCursor {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
}

function shiftMonth(cursor: MonthCursor, delta: number): MonthCursor {
  // Build a Date and let it normalize month overflow/underflow.
  const d = new Date(cursor.year, cursor.month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

function sameYearMonth(d: Date, c: MonthCursor): boolean {
  return d.getFullYear() === c.year && d.getMonth() === c.month;
}

function formatTimeRange(start: Date, end?: Date | null): string {
  const fmt = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (!end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatLongDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function SeminarsAndEventsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>('calendar');
  const [cursor, setCursor] = useState<MonthCursor>(todayCursor);
  const [events, setEvents] = useState<SeminarEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Single fetch on mount: backend returns all whitelisted seminar/
  // event rows sorted by StartDate. The page filters by month
  // client-side as the user pages through the cursor — this keeps
  // navigation snappy and avoids re-querying for every chevron tap.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    projectEssentialApi
      .getEvents()
      .then((res) => {
        if (mounted) setEvents(res.items);
      })
      .catch((err) => {
        if (mounted) setError((err as Error)?.message || 'Failed to load events');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Events that fall within the cursor month, sorted ascending. We also
  // apply the seminars-page category whitelist here so the calendar
  // grid, the count chip and the agenda list all see the same dataset.
  // `start` is nullable on the API; rows without a start date can't be
  // placed on a calendar so we drop them.
  const monthEvents = useMemo(() => {
    return events
      .filter((e) => e.category != null && ALLOWED_EVENT_CATEGORIES.has(e.category))
      .filter((e): e is SeminarEventItem & { start: string } => e.start != null)
      .map((e) => ({ ...e, _start: new Date(e.start), _end: e.end ? new Date(e.end) : null }))
      .filter((e) => sameYearMonth(e._start, cursor))
      .sort((a, b) => a._start.getTime() - b._start.getTime());
  }, [cursor, events]);

  // Map<dayOfMonth, count> for the month-grid badges.
  const eventCountByDay = useMemo(() => {
    const map = new Map<number, number>();
    for (const e of monthEvents) {
      const day = e._start.getDate();
      map.set(day, (map.get(day) ?? 0) + 1);
    }
    return map;
  }, [monthEvents]);

  return (
    <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      <PePageHeader title={<>Seminars &amp; Events</>} />

      <ProjectNavButtons active="seminars" />

      {/* View toggle — right-aligned split button. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, next: ViewMode | null) => {
            // Ignore deselect taps so one option is always active.
            if (next) setView(next);
          }}
          aria-label="View mode"
          size="small"
          sx={{
            // Joined split-button look: shared border, rounded outer
            // corners, no double-border between segments.
            borderRadius: 1.5,
            overflow: 'hidden',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.78rem', sm: '0.85rem' },
              px: { xs: 1.25, sm: 1.75 },
              py: 0.5,
              border: '1px solid var(--color-border-strong)',
              color: 'var(--color-text-strong)',
              bgcolor: 'var(--color-surface)',
              '&.Mui-selected': {
                bgcolor: 'var(--color-accent)',
                color: '#fff',
                borderColor: 'var(--color-accent)',
                '&:hover': {
                  bgcolor: 'var(--color-accent-strong, var(--color-accent))',
                },
              },
              '&:not(.Mui-selected):hover': {
                bgcolor: 'var(--color-accent-soft)',
                borderColor: 'var(--color-accent)',
              },
            },
          }}
        >
          <ToggleButton value="calendar" aria-label="Calendar view">
            <CalendarMonthIcon fontSize="small" sx={{ mr: 0.75 }} />
            Calendar
          </ToggleButton>
          <ToggleButton value="list" aria-label="List view">
            <ViewListIcon fontSize="small" sx={{ mr: 0.75 }} />
            List
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Month pagination header */}
      <MonthPager
        cursor={cursor}
        onPrev={() => setCursor((c) => shiftMonth(c, -1))}
        onNext={() => setCursor((c) => shiftMonth(c, 1))}
        eventCount={monthEvents.length}
      />

      {/* Loading / error banners — shown above the body so the cursor
          and view toggle stay interactive even while the first fetch
          is in flight. */}
      {loading && events.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && events.length === 0 && !loading && (
        <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
          {error}
        </Typography>
      )}

      {/* Body */}
      {view === 'calendar' ? (
        <CalendarView cursor={cursor} eventCountByDay={eventCountByDay} />
      ) : null}

      {/* Event list — always rendered. In calendar view it acts as the
          month's full agenda below the grid; in list view it's the only
          content surface. */}
      <EventList
        events={monthEvents.map((e) => ({
          id: e.id,
          name: e.name,
          start: e._start,
          end: e._end,
        }))}
        emptyLabel={`No events scheduled for ${MONTH_NAMES[cursor.month]} ${cursor.year}.`}
      />
    </Stack>
  );
}

// ── Month pager ────────────────────────────────────────────────────────

function MonthPager({
  cursor,
  onPrev,
  onNext,
  eventCount,
}: {
  cursor: MonthCursor;
  onPrev: () => void;
  onNext: () => void;
  eventCount: number;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        bgcolor: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-border-strong)',
        borderRadius: 1.5,
        px: { xs: 1, sm: 1.5 },
        py: { xs: 0.75, sm: 1 },
      }}
    >
      <IconButton
        onClick={onPrev}
        aria-label="Previous month"
        size="small"
        sx={{ color: 'var(--color-accent)' }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: 'var(--color-text-strong)',
            letterSpacing: '0.02em',
          }}
        >
          {MONTH_NAMES[cursor.month]} {cursor.year}
        </Typography>
        <Chip
          size="small"
          label={`${eventCount} ${eventCount === 1 ? 'event' : 'events'}`}
          sx={{
            bgcolor:
              eventCount > 0 ? 'var(--color-accent)' : 'var(--color-surface)',
            color: eventCount > 0 ? '#fff' : 'var(--color-text-muted)',
            fontWeight: 700,
            border:
              eventCount > 0
                ? '1px solid var(--color-accent)'
                : '1px solid var(--color-border-strong)',
          }}
        />
      </Stack>
      <IconButton
        onClick={onNext}
        aria-label="Next month"
        size="small"
        sx={{ color: 'var(--color-accent)' }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
}

// ── Calendar view ──────────────────────────────────────────────────────

function CalendarView({
  cursor,
  eventCountByDay,
}: {
  cursor: MonthCursor;
  eventCountByDay: Map<number, number>;
}) {
  // Build the grid: leading blanks for the day-of-week of the 1st, then
  // 1..N for the month, then trailing blanks to fill the final week.
  const cells = useMemo(() => {
    const firstWeekday = new Date(cursor.year, cursor.month, 1).getDay();
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const arr: Array<{ key: string; day: number | null }> = [];
    for (let i = 0; i < firstWeekday; i += 1) {
      arr.push({ key: `pad-start-${i}`, day: null });
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      arr.push({ key: `d-${d}`, day: d });
    }
    while (arr.length % 7 !== 0) {
      arr.push({ key: `pad-end-${arr.length}`, day: null });
    }
    return arr;
  }, [cursor]);

  const today = new Date();
  const isCurrentMonth =
    today.getFullYear() === cursor.year && today.getMonth() === cursor.month;

  return (
    <Box
      sx={{
        border: '1px solid var(--color-border-strong)',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'var(--color-surface)',
      }}
    >
      {/* Weekday header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          bgcolor: 'var(--color-surface-elevated)',
          borderBottom: '1px solid var(--color-border-strong)',
        }}
      >
        {WEEKDAY_LABELS.map((d) => (
          <Box
            key={d}
            sx={{
              py: 0.75,
              textAlign: 'center',
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
            }}
          >
            {d}
          </Box>
        ))}
      </Box>
      {/* Day cells */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((cell, idx) => {
          const isPad = cell.day === null;
          const count = !isPad ? eventCountByDay.get(cell.day!) ?? 0 : 0;
          const hasEvents = count > 0;
          const isToday =
            !isPad && isCurrentMonth && cell.day === today.getDate();
          // Right border on every cell except the last column; bottom
          // border on every cell except the last row.
          const isLastCol = idx % 7 === 6;
          const isLastRow = idx >= cells.length - 7;
          return (
            <Box
              key={cell.key}
              sx={{
                minHeight: { xs: 56, sm: 72 },
                p: 0.75,
                position: 'relative',
                borderRight: isLastCol ? 'none' : '1px solid var(--color-border)',
                borderBottom: isLastRow ? 'none' : '1px solid var(--color-border)',
                bgcolor: isPad ? 'var(--color-surface-elevated)' : 'transparent',
                opacity: isPad ? 0.5 : 1,
              }}
            >
              {!isPad && (
                <Stack spacing={0.5} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 22,
                      height: 22,
                      borderRadius: '50%',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      alignSelf: 'flex-start',
                      bgcolor: isToday ? 'var(--color-accent)' : 'transparent',
                      color: isToday ? '#fff' : 'var(--color-text-strong)',
                    }}
                  >
                    {cell.day}
                  </Box>
                  {hasEvents && (
                    <Chip
                      size="small"
                      icon={<EventIcon style={{ fontSize: 14 }} />}
                      label={count}
                      sx={{
                        height: 20,
                        alignSelf: 'flex-start',
                        bgcolor: 'var(--color-accent-soft)',
                        color: 'var(--color-accent-strong)',
                        fontWeight: 700,
                        '& .MuiChip-icon': { color: 'var(--color-accent-strong)', ml: 0.5 },
                        '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                      }}
                    />
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Event list ─────────────────────────────────────────────────────────

interface ListEvent {
  id: string;
  name: string;
  start: Date;
  end: Date | null;
}

function EventList({
  events,
  emptyLabel,
}: {
  events: ListEvent[];
  emptyLabel: string;
}) {
  if (events.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 2 }}>
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Stack spacing={1.25}>
      {events.map((e) => (
        <Box
          key={e.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.25, sm: 1.5 },
            px: { xs: 1.25, sm: 1.5 },
            py: { xs: 1, sm: 1.25 },
            borderRadius: 1.5,
            border: '1.5px solid var(--color-border-strong)',
            bgcolor: 'var(--color-surface)',
          }}
        >
          {/* Date block */}
          <Box
            sx={{
              minWidth: 56,
              textAlign: 'center',
              borderRadius: 1.25,
              border: '1px solid var(--color-accent)',
              color: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
              py: 0.5,
              px: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ display: 'block', fontWeight: 700, lineHeight: 1, letterSpacing: '0.06em' }}
            >
              {e.start.toLocaleString(undefined, { month: 'short' }).toUpperCase()}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, lineHeight: 1.1 }}
            >
              {e.start.getDate()}
            </Typography>
          </Box>
          {/* Name + datetime */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: 'var(--color-text-strong)',
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={e.name}
            >
              {e.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'var(--color-text-muted)', display: 'block' }}
            >
              {formatLongDate(e.start)} · {formatTimeRange(e.start, e.end)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
